import { useQuery } from '@tanstack/react-query'
import { categoriesApi } from '@/api/endpoints'
import { toBase } from '@/lib/pricing'

/**
 * Category minimum orders, for screens that build a line before it is saved.
 *
 * The minimum lives on the category, in its products' base unit (kg). Once a
 * line is saved the server says whether it is short; this is only for the
 * moment before — the staff New order drawer and the Add product picker — so
 * the amount starts at the minimum and a warning shows as it is typed.
 *
 * Same cache as the Categories screen, so this costs no extra request there.
 */
export function useCategoryMinimums() {
  const { data } = useQuery({
    queryKey: ['category-management'],
    queryFn: categoriesApi.list,
    staleTime: 60_000,
  })
  const byName = new Map()
  for (const c of data ?? []) {
    const min = Number(c.min_order_qty)
    if (Number.isFinite(min) && min > 0) byName.set(c.name, min)
  }
  return byName
}

/** This product's minimum in its base unit, or null. */
export const minimumFor = (product, minimums) =>
  (product?.category && minimums.get(product.category)) || null

/** The starting quantity for a new line: the minimum, else 1. */
export const startingQuantity = (product, minimums) => minimumFor(product, minimums) ?? 1

/**
 * Is this amount short of the product's minimum? False when there is none,
 * or when the unit cannot be converted here (a bag priced by its category's
 * weight) — the server decides those once the line is saved.
 */
export function isShort(product, quantity, unit, minimums) {
  const min = minimumFor(product, minimums)
  const amount = Number(quantity)
  if (min == null || !Number.isFinite(amount) || amount <= 0) return false
  const base = toBase(product, amount, unit)
  return base != null && base < min
}

/** '25 kg' — a minimum as a person reads it. */
export const describeMinimum = (product, minimums) => {
  const min = minimumFor(product, minimums)
  if (min == null) return null
  const unit = String(product.base_unit ?? 'KILOGRAMS').toUpperCase()
  return `${min} ${unit === 'KILOGRAMS' ? 'kg' : unit.toLowerCase()}`
}

/**
 * What + and − move a new line by, in `unit`: the category's minimum —
 * 25 kg, or 1 in 25 kg bags. Undefined with no minimum, or a unit this
 * screen cannot convert (the server still rounds those on save).
 */
export function stepFor(product, unit, minimums) {
  const min = minimumFor(product, minimums)
  if (min == null) return undefined
  const perUnit = toBase(product, 1, unit || product.base_unit)
  return perUnit > 0 ? min / perUnit : undefined
}

